import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api'; 
import LeaveUseModal from '../components/LeaveUseModal'; 
import { useAuth } from '../contexts/AuthContext'; 
import { useDirtyForm } from '../contexts/DirtyFormContext';

// ⬇️ [모바일 고정 해제 1/3] baseStyles.filtersContainer.position을 'relative'로 변경
const baseStyles = {
  filtersContainer: { 
    background: '#f8f9fa', 
    padding: '15px', 
    borderRadius: '5px',
    position: 'relative', // ⬅️ 'sticky'에서 'relative'로 변경 (모바일 기본값)
    top: 0, 
    zIndex: 10, 
    borderBottom: '1px solid #dee2e6'
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  filterLabel: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
    color: '#555',
    paddingLeft: '2px',
  },
  select: { padding: '8px', fontSize: '1rem', width: '160px' }, 
  searchInput: { padding: '8px', fontSize: '1rem', width: '160px' }, 
  tableContainer: { marginTop: '20px', overflowX: 'auto' },
  table: { width: '100%', minWidth: '1400px', borderCollapse: 'collapse' }, 
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', fontSize: '0.9rem', whiteSpace: 'nowrap', textAlign: 'center', cursor: 'pointer' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.9rem', textAlign: 'center' },
  input: { width: '80px', padding: '5px', boxSizing: 'border-box' }, 
  inputRight: { width: '80px', padding: '5px', boxSizing: 'border-box', textAlign: 'right' },
  button: { padding: '5px 10px', cursor: 'pointer', border: 'none', borderRadius: '4px' },
  saveButton: { backgroundColor: '#28a745', color: 'white' },
  buttonDisabled: { backgroundColor: '#6c757d', color: '#e9ecef', cursor: 'not-allowed' },
  modalButton: { backgroundColor: '#17a2b8', color: 'white' },
  excelButton: { backgroundColor: '#007bff', color: 'white', padding: '8px 12px', fontSize: '1rem', marginLeft: 'auto' },
  statusButton: { padding: '8px 12px', fontSize: '1rem', marginLeft: '10px' },
  cancelButton: { backgroundColor: '#dc3545', color: 'white' },
  countDisplay: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#333',
    marginTop: '15px', 
    paddingLeft: '5px' 
  }
};

const LeaveManagePage = () => {
  const { user } = useAuth(); 
  const { setIsDirty } = useDirtyForm();

  // ( ... state 선언 ... - 변경 없음)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('전체');
  const [searchTerms, setSearchTerms] = useState({
    searchName: '',
    searchWorkplace: '',
    searchDistrict: '',
  });
  const [appliedSearch, setAppliedSearch] = useState({
    searchName: '',
    searchWorkplace: '',
    searchDistrict: '',
  });
  const [leaveData, setLeaveData] = useState([]); 
  const [editingData, setEditingData] = useState({}); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isMonthClosed, setIsMonthClosed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState({ id: null, details: [], version: 0 });
  const [currentlyEditingRowId, setCurrentlyEditingRowId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ sortBy: null, sortOrder: 'ASC' });
  const [hasSearched, setHasSearched] = useState(false);

  // ⬇️ [모바일 고정 해제 2/3] windowWidth state 추가
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // ⬇️ [모바일 고정 해제] windowWidth useEffect 추가
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ⬇️ [모바일 고정 해제 3/3] 동적 styles 변수 (position 속성 추가)
  const styles = {
    ...baseStyles,
    filtersContainer: {
      ...baseStyles.filtersContainer,
      // 1024px 초과 (데스크탑)일 때만 'sticky'로 변경하고 top: 60px
      position: windowWidth > 1024 ? 'sticky' : 'relative',
      top: windowWidth > 1024 ? '60px' : '0px', 
    }
  };

  // ( ... fetchProjects ... - 변경 없음)
  const fetchProjects = useCallback(async () => {
    if (!selectedYear) {
      setProjects([]);
      setSelectedProject('all'); 
      return;
    }
    try {
      const res = await api.get(`/projects?year=${selectedYear}`);
      setProjects(res.data);
    } catch (err) { 
      console.error("사업 목록 로딩 실패", err); 
      setProjects([]);
      setSelectedProject('all');
    }
  }, [selectedYear]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    if (projects.length > 0) {
      const projectIds = projects.map(p => p.id);
      // '전체' 선택 시 유지, 그 외 선택한 사업이 목록에 없으면 '전체'로 초기화
      if (selectedProject !== 'all' && !projectIds.includes(Number(selectedProject))) {
        setSelectedProject('all');
      }
    } else {
      setSelectedProject('all');
    }
  }, [projects]);


  // ( ... fetchLeaveRecords ... - 변경)
  const fetchLeaveRecords = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    setError('');
    setMessage('');
    setIsDirty(false); 
    setCurrentlyEditingRowId(null);
    try {
      const params = {
        year: selectedYear,
        projectId: selectedProject,
        ...appliedSearch,
      };
      // '전체'가 아닐 때만 month 파라미터 추가
      if (selectedMonth !== '전체') {
        params.month = selectedMonth;
      } else {
        params.month = 'all'; // 전체 선택 시 'all' 전송
      }
      if (sortConfig.sortBy) {
        params.sortBy = sortConfig.sortBy;
        params.sortOrder = sortConfig.sortOrder;
      }
      const res = await api.get('/leave', { params });
      
      setLeaveData(res.data); 
      
      const initialEditingData = {};
      res.data.forEach(item => {
        if (item.leaveRecord) {
          // leaveRecord.id가 없을 경우 participant.id를 키로 사용 (전체 선택 시)
          const key = item.leaveRecord.id || `participant_${item.participant.id}`;
          initialEditingData[key] = {
            usedLeaveDetails: item.leaveRecord.usedLeaveDetails || [],
            payoutHours: item.leaveRecord.payoutHours || 0,
            version: item.leaveRecord.version || 0,
          };
        }
      });
      setEditingData(initialEditingData); 

      const closed = res.data.length > 0 && res.data[0].leaveRecord?.status === 'closed';
      setIsMonthClosed(closed);
    } catch (err) { 
      setError(err.response?.data?.message || '연차 기록 로딩 실패');
      setLeaveData([]);
      setEditingData({});
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedProject, selectedMonth, appliedSearch, sortConfig, setIsDirty]);

  // ( ... useEffect (데이터 로드) ... - 검색 이후에만 조회)
  useEffect(() => {
    if (hasSearched) {
      fetchLeaveRecords();
    }
  }, [fetchLeaveRecords, hasSearched]);

  // ( ... 'Smart Dirty' useEffect ... - 변경 없음)
  useEffect(() => {
    if (leaveData.length === 0) {
      setIsDirty(false); 
      return;
    }
    let isActuallyDirty = false;
    for (const item of leaveData) {
      const originalRecord = item.leaveRecord;
      if (!originalRecord) continue; 
      const editingRecord = editingData[originalRecord.id];
      if (!editingRecord) continue;
      const originalHours = String(originalRecord.payoutHours || 0);
      const editedHours = String(editingRecord.payoutHours || 0);
      const originalDetails = JSON.stringify(originalRecord.usedLeaveDetails || []);
      const editedDetails = JSON.stringify(editingRecord.usedLeaveDetails || []);
      if (originalHours !== editedHours || originalDetails !== editedDetails) {
        isActuallyDirty = true;
        break;
      }
    }
    setIsDirty(isActuallyDirty);
  }, [editingData, leaveData, setIsDirty]);

  // ( ... handleSearchChange ... - 변경 없음)
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerms(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchClick = () => {
    setAppliedSearch(searchTerms);
    setHasSearched(true);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  // 정렬 핸들러 (같은 열: 오름차순 → 내림차순 토글)
  const handleSort = (columnName) => {
    let sortOrder = 'ASC';
    if (sortConfig.sortBy === columnName && sortConfig.sortOrder === 'ASC') {
      sortOrder = 'DESC';
    }
    setSortConfig({ sortBy: columnName, sortOrder });
  };

  /** 정렬 가능 열: 미선택 ↕, 선택 시 ↑ / ↓ */
  const sortIndicator = (columnKey) => {
    if (sortConfig.sortBy !== columnKey) return ' ↕';
    return sortConfig.sortOrder === 'ASC' ? ' ↑' : ' ↓';
  };
  
  // ( ... handleEditChange ... - 변경 없음)
  const handleEditChange = (leaveRecordId, field, value) => {
    setCurrentlyEditingRowId(leaveRecordId);
    setEditingData(prev => ({ ...prev, [leaveRecordId]: { ...prev[leaveRecordId], [field]: value } }));
  };
  
  // ( ... handleSaveRow ... - 변경 없음)
  const handleSaveRow = async (leaveRecordId) => {
    const dataToSave = editingData[leaveRecordId];
    if (!dataToSave) return;
    try {
      await api.put(`/leave/${leaveRecordId}`, {
        usedLeaveDetails: dataToSave.usedLeaveDetails, 
        payoutHours: dataToSave.payoutHours,
        version: dataToSave.version,
      });
      setMessage('저장되었습니다.');
      fetchLeaveRecords(); 
    } catch (err) {
      setIsDirty(true); 
      if (err.response && err.response.status === 409) {
        setError(err.response.data.message);
        setMessage('오류: 다른 사용자가 먼저 수정했습니다. 데이터를 새로고침합니다.');
        setTimeout(() => fetchLeaveRecords(), 2000); 
      } else {
        setError(err.response?.data?.message || '저장 실패');
      }
    }
  };

  // ( ... handleCancelEdit ... - 변경 없음)
  const handleCancelEdit = () => {
    if (window.confirm('현재 행의 수정사항을 취소하고 원래 값으로 되돌리시겠습니까?')) {
      fetchLeaveRecords(); 
    }
  };
  
  // ( ... handleLeaveUseClick ... - 변경 없음)
  const handleLeaveUseClick = (leaveRecordId) => {
    if (currentlyEditingRowId !== null && currentlyEditingRowId !== leaveRecordId) {
      alert('먼저 수정 중인 항목을 저장 또는 취소해주세요.');
      return;
    }
    setCurrentlyEditingRowId(leaveRecordId);
    const record = editingData[leaveRecordId];
    setModalTarget({ 
      id: leaveRecordId, 
      details: record?.usedLeaveDetails || [],
      version: record?.version || 0
    });
    setIsModalOpen(true);
  };
  
  // ( ... handleSaveLeaveUse ... - 변경 없음)
  const handleSaveLeaveUse = async (leaveRecordId, leaveData) => {
    try {
      await api.put(`/leave/${leaveRecordId}`, leaveData);
      setMessage('연차 사용 내역이 저장되었습니다.');
      fetchLeaveRecords(); 
    } catch (err) {
      setIsDirty(true); 
      if (err.response && err.response.status === 409) {
        setError(err.response.data.message);
        setMessage('오류: 다른 사용자가 먼저 수정했습니다. 데이터를 새로고침합니다.');
        setTimeout(() => fetchLeaveRecords(), 2000);
      } else {
        setError(err.response?.data?.message || '연차 사용 내역 저장 실패');
      }
    }
  };
  
  // ( ... handleCloseModal ... - 변경 없음)
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentlyEditingRowId(null); 
  };
  
  // ( ... handleLeaveDetailsChangeInModal ... - 변경 없음)
  const handleLeaveDetailsChangeInModal = (newDetails) => {
    const leaveRecordId = modalTarget.id;
    if (!leaveRecordId) return;
    setCurrentlyEditingRowId(leaveRecordId); 
    setEditingData(prev => ({
      ...prev,
      [leaveRecordId]: { ...prev[leaveRecordId], usedLeaveDetails: newDetails }
    }));
  };

  // ( ... handleMonthStatusToggle, handleExcelExport ... - 변경 없음)
  const handleExcelExport = useCallback(async () => {
    if (currentlyEditingRowId !== null) {
      alert('수정 중인 항목을 먼저 저장하거나 취소한 후 엑셀 출력을 시도해주세요.');
      return;
    }
    try {
      const params = { 
        year: selectedYear, 
        projectId: selectedProject, 
        ...searchTerms 
      };
      // '전체'가 아닐 때만 month 파라미터 추가
      if (selectedMonth !== '전체') {
        params.month = selectedMonth;
      } else {
        params.month = 'all'; // 전체 선택 시 'all' 전송
      }
      const res = await api.get('/leave/export', {
        params,
        responseType: 'blob' 
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = selectedMonth === '전체' 
        ? `leave_records_${selectedYear}_전체.xlsx`
        : `leave_records_${selectedYear}_${selectedMonth}.xlsx`;
      link.setAttribute('download', filename); 
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || '엑셀 출력 실패');
    }
  }, [selectedYear, selectedProject, selectedMonth, searchTerms, currentlyEditingRowId]);

  // --- 렌더링용 ---
  const monthOptions = ['전체', ...Array.from({length: 12}, (_, i) => i + 1)];

  return (
    <div>
      <h2>참여자 연차 현황</h2>

      {/* 1. 필터 */}
      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>연도</span>
            <input
              type="number"
              style={styles.select} 
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              placeholder="연도 입력 (예: 2024)"
              min="2020"
              step="1"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사업명</span>
            <select style={styles.select} value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
              <option value="all">전체</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.projectName}</option>)}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>월</span>
            <select style={styles.select} value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
              {monthOptions.map(m => (
                <option key={m} value={m}>
                  {m === '전체' ? '전체' : `${m}월`}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>성명</span>
            <input type="text" name="searchName" placeholder="성명 검색" value={searchTerms.searchName} onChange={handleSearchChange} onKeyPress={handleSearchKeyPress} style={styles.searchInput} />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>근무장소</span>
            <input
              type="text"
              name="searchWorkplace"
              placeholder="근무장소 검색"
              value={searchTerms.searchWorkplace}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>읍면동</span>
            <input
              type="text"
              name="searchDistrict"
              placeholder="읍면동 검색"
              value={searchTerms.searchDistrict}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              style={styles.searchInput}
            />
          </div>
          <div style={{...styles.filterItem, marginLeft: 'auto', flexDirection: 'row', gap: '8px' }}>
            <button
              style={styles.searchButton}
              onClick={handleSearchClick}
              disabled={loading}
            >
              🔍 검색
            </button>
            <button
              style={{...styles.button, ...styles.excelButton, marginLeft: 0}}
              onClick={handleExcelExport}
              disabled={loading || leaveData.length === 0 || currentlyEditingRowId !== null}
            >
              엑셀 출력
            </button>
          </div>
        </div>

        {/* 인원 표시 ( ... 변경 없음 ... ) */}
        <div style={styles.countDisplay}>
          {loading ? '검색 중...' : `조회된 인원: 총 ${leaveData.length} 명`}
        </div>

      </div>
      
      {message && <p style={{color: 'green', marginTop: '10px'}}>{message}</p>}
      {error && <p style={{color: 'red', marginTop: '10px'}}>{error}</p>}

      {/* 2. 데이터 테이블 ( ... 변경 없음 ... ) */}
      <div style={styles.tableContainer}>
        {loading ? <p>로딩 중...</p> : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th} onClick={() => handleSort('name')}>
                  성명{sortIndicator('name')}
                </th>
                <th style={styles.th}>주민번호</th>
                <th style={styles.th}>사업명</th>
                <th style={styles.th} onClick={() => handleSort('workplace')}>
                  근무장소{sortIndicator('workplace')}
                </th>
                <th style={styles.th}>발생연차(누적)</th>
                <th style={styles.th}>사용연차(누적)</th>
                <th style={styles.th} onClick={() => handleSort('remainingHours')}>
                  잔여연차{sortIndicator('remainingHours')}
                </th>
                <th style={styles.th} onClick={() => handleSort('pendingLeaveHours')}>
                  승인중 연차{sortIndicator('pendingLeaveHours')}
                </th>
                <th style={styles.th} onClick={() => handleSort('month')}>
                  연차사용날짜(월){sortIndicator('month')}
                </th>
                <th style={styles.th}>기지급수당(시간)</th>
                <th style={styles.th} onClick={() => handleSort('payoutAmount')}>
                  정산지급액{sortIndicator('payoutAmount')}
                </th>
                <th style={styles.th} onClick={() => handleSort('adjustedLeave')}>
                  미정산연차{sortIndicator('adjustedLeave')}
                </th>
              </tr>
            </thead>
            <tbody>
              {leaveData.length > 0 ? leaveData.map(item => {
                if (!item.leaveRecord) return null; 
                const p = item.participant;
                const originalRecord = item.leaveRecord;
                const lr = item.leaveRecord;
                // leaveRecord.id가 없을 경우 participant.id를 키로 사용 (전체 선택 시)
                const editingKey = originalRecord.id || `participant_${p.id}`;
                const editing = editingData[editingKey] || {};
                const originalHours = String(originalRecord.payoutHours || 0);
                const editedHours = String(editing.payoutHours || 0);
                const originalDetails = JSON.stringify(originalRecord.usedLeaveDetails || []);
                const editedDetails = JSON.stringify(editing.usedLeaveDetails || []);
                const isRowDirty = (originalHours !== editedHours) || (originalDetails !== editedDetails);
                const isAnotherRowEditing = currentlyEditingRowId !== null && currentlyEditingRowId !== editingKey;

                return (
                  <tr key={p.id} style={{backgroundColor: isAnotherRowEditing ? '#f8f9fa' : 'transparent'}}>
                    <td style={styles.td}>{p.name}</td>
                    <td style={styles.td}>{p.rrn.substring(0, 8)}...</td>
                    <td style={styles.td}>{p.Project?.projectName || p.project?.projectName || p.businessName || '-'}</td>
                    <td style={styles.td}>{p.workplace}</td>
                    <td style={styles.td}>{item.totalGeneratedHours} h</td>
                    <td style={styles.td}>{item.cumulativeUsedHours} h</td>
                    <td style={styles.td}>{item.remainingHours} h</td>
                    <td style={styles.td}>
                      <span style={(item.pendingLeaveHours || 0) > 0 ? { color: 'red', fontWeight: 'bold' } : {}}>
                        {(item.pendingLeaveHours || 0) > 0 ? `${item.pendingLeaveHours} h` : '-'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {(editing.usedLeaveDetails || originalRecord.usedLeaveDetails || []).map(detail => {
                        const date = new Date(detail.date);
                        const month = date.getMonth() + 1;
                        const day = date.getDate();
                        return `${month}-${day}(${detail.hours}h)`;
                      }).join(', ') || '-'}
                    </td>
                    <td style={styles.td}>
                      <input
                        type="number"
                        style={styles.inputRight}
                        step="1"
                        value={editing.payoutHours || 0}
                        disabled={true} // 항상 비활성화 - 조회 전용
                        readOnly={true} // 읽기 전용
                      />
                    </td>
                    <td style={styles.td}>{item.payoutAmount.toLocaleString()} 원</td>
                    <td style={styles.td}>{item.adjustedLeave} h</td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="12" style={{...styles.td, textAlign: 'center'}}>
                    {error || '조회된 데이터가 없습니다.'} 
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* 3. 모달 ( ... 변경 없음 ... ) */}
      <LeaveUseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveLeaveUse}
        onDetailsChange={handleLeaveDetailsChangeInModal} 
        currentLeaveDetails={modalTarget.details}
        leaveRecordId={modalTarget.id}
        version={modalTarget.version}
        isMonthClosed={isMonthClosed} 
      />
    </div>
  );
};

export default LeaveManagePage;